import React, { useEffect, useState } from 'react';
import { FiSearch, FiMapPin, FiNavigation, FiImage } from 'react-icons/fi';
import { FaFileExcel, FaSpinner } from 'react-icons/fa';
import toast from 'react-hot-toast';
import axiosInstance from '../../../libs/axios';
import { Pagination, PaginationType } from '../../../utils/pagination.utils';
import { ExcelDownload } from '../../../utils/excel.utils';
import { dateRangeError, dateRangeInputBounds } from '../../../utils/date-range.utils';
import { SelectWithSearch2, type SelectOption } from '../../../utils/select-with-search';

interface LocationTrailPoint {
  point: string;
  label: string;
  latitude: number | null;
  longitude: number | null;
  address: string | null;
  distance_to_next_km?: number | null;
  distance_from_previous_km?: number | null;
  site_id?: string | null;
  site_name?: string | null;
}

/** Postgres DECIMAL columns arrive as strings, so every numeric field is widened. */
type Numeric = number | string | null | undefined;

interface CabClaimRow {
  id: number;
  engineer: { id: number; name: string; employee_code: string; association_type: string } | null;
  project_name?: string | null;
  trip_purpose: string;
  trip_start_date?: string | null;
  trip_end_date?: string | null;
  start_address?: string | null;
  end_address?: string | null;
  vehicle_number?: string | null;
  driver_name?: string | null;
  location_trail: LocationTrailPoint[];
  distances: {
    reporting_to_checkin_km: Numeric;
    checkin_to_start_km: Numeric;
    end_to_checkout_km: Numeric;
    checkout_to_reporting_km: Numeric;
    total_route_km: Numeric;
    running_kms: Numeric;
  };
  cab_readings: { start_reading: Numeric; end_reading: Numeric; running_kms: Numeric };
  amounts: {
    toll_charges: Numeric;
    other_charges?: Numeric;
    hiring_total_amount: Numeric;
    hiring_per_km_amount: Numeric;
    approved_amount?: Numeric;
  };
  visits_count: number;
  status: string;
  approver_name?: string | null;
  approved_at?: string | null;
  photos?: {
    bill_photo?: string | null;
    toll_photo?: string | null;
    cab_start_number_selfie?: string | null;
    cab_start_model_selfie?: string | null;
    cab_end_number_selfie?: string | null;
    cab_end_model_selfie?: string | null;
  };
}

interface NormalizedPagination {
  total_items: number;
  current_page: number;
  total_pages: number;
  items_per_page: number;
  has_next_page: boolean;
  has_previous_page: boolean;
}

const TABLE_COLUMN_COUNT = 15;

// cab_hirings.status — the values the API accepts and returns.
const STATUS_OPTIONS = [
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'claim_submitted', label: 'Claim Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
];
const statusLabel = (value?: string | null) =>
  STATUS_OPTIONS.find((o) => o.value === value)?.label || (value || '-').replace(/_/g, ' ');

// /users/list is already limited to people sharing a project with the caller
// (level 1 sees everyone) — the same scope the report itself applies.
const mapEngineerOption = (user: any): SelectOption => {
  const name = [user.first_name, user.last_name].filter(Boolean).join(' ') || user.email || `ID ${user.id}`;
  return { id: user.id, value: user.id, label: user.employee_code ? `${name} (${user.employee_code})` : name };
};

interface ReportFilters {
  search: string;
  status: string;
  dateFrom: string;
  dateTo: string;
  engineer: SelectOption | null;
}
const EMPTY_FILTERS: ReportFilters = { search: '', status: '', dateFrom: '', dateTo: '', engineer: null };

/** Query params for the API from the APPLIED filters (never the draft form). */
const buildParams = (filters: ReportFilters, targetPage: number, targetLimit: number) => {
  const params: any = { page: targetPage, limit: targetLimit };
  if (filters.search.trim()) params.search = filters.search.trim();
  if (filters.status) params.status = filters.status;
  if (filters.dateFrom) params.date_from = filters.dateFrom;
  if (filters.dateTo) params.date_to = filters.dateTo;
  if (filters.engineer) params.engineer_id = filters.engineer.value;
  return params;
};

/** Coerces DECIMAL-as-string into a real number. Keeps 0; drops null/''/NaN. */
const toNumber = (value: Numeric): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/** Renders a number, preserving 0 (the old `|| '-'` turned 0 into a dash). */
const showNumber = (value: Numeric, fallback = '-') => {
  const num = toNumber(value);
  return num === null ? fallback : num.toLocaleString('en-IN');
};

const formatAmount = (value: Numeric, fallback = '-') => {
  const num = toNumber(value);
  return num === null
    ? fallback
    : `₹${num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * DATEONLY values ("2026-07-01") must not go through `new Date()` — that parses
 * as UTC midnight and renders as the previous day for anyone west of UTC.
 * Real timestamps are rendered in IST.
 */
const formatDateValue = (value?: string | null, fallback = '-') => {
  if (!value) return fallback;
  const str = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [year, month, day] = str.split('-');
    return `${day}/${month}/${year}`;
  }
  const date = new Date(str);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' });
};

const CabHiringClaimReport: React.FC = () => {
  const [claims, setClaims] = useState<CabClaimRow[]>([]);
  const [pagination, setPagination] = useState<NormalizedPagination>({ total_items: 0, current_page: 1, total_pages: 1, items_per_page: 10, has_next_page: false, has_previous_page: false });
  const [loading, setLoading] = useState(false);
  // What the form shows (being edited) vs what the table and export use.
  const [draft, setDraft] = useState<ReportFilters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<ReportFilters | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);
  const [showFilterForm, setShowFilterForm] = useState(true);
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [exportData, setExportData] = useState<any[]>([]);
  const [exporting, setExporting] = useState(false);
  const hasSearched = applied !== null;
  const setDraftField = (patch: Partial<ReportFilters>) => setDraft((prev) => ({ ...prev, ...patch }));

  // The one place the table loads from: applied filters + page + page size.
  // Ignores a response that arrives after a newer request was sent.
  useEffect(() => {
    if (!applied) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/reports/claims/cab-hiring', { params: buildParams(applied, page, limit) });
        if (cancelled) return;
        if (response.data.success) {
          const payload = response.data.data || { claims: [] };
          const rows = payload.claims || [];
          setClaims(rows);
          const p = payload.pagination || {};
          const total = Number(p.total_items ?? p.total_count) || 0;
          const pages = Math.max(1, Number(p.total_pages) || Math.ceil(total / limit));
          setPagination({
            total_items: total,
            current_page: page,
            total_pages: pages,
            items_per_page: limit,
            has_next_page: page < pages,
            has_previous_page: page > 1,
          });
        } else {
          setClaims([]);
          toast.error(response.data.message || 'Failed to fetch cab hiring claim reports');
        }
      } catch (error) {
        if (cancelled) return;
        console.error(error);
        setClaims([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applied, page, limit]);

  const mapClaimForExport = (c: CabClaimRow) => ({
    ID: c.id,
    'Employee Code': c.engineer?.employee_code || '',
    Engineer: c.engineer?.name || '',
    Association: c.engineer?.association_type || '',
    'Project Name': c.project_name || '',
    'Trip Purpose': c.trip_purpose || '',
    'Vehicle Number': c.vehicle_number || '',
    'Driver Name': c.driver_name || '',
    'Start Location': c.start_address || '',
    'End Location': c.end_address || '',
    Visits: c.visits_count ?? '',
    'System Calc. Distance (km)': toNumber(c.distances?.total_route_km) ?? '',
    'Trip Start Date': formatDateValue(c.trip_start_date, ''),
    'Trip End Date': formatDateValue(c.trip_end_date, ''),
    Status: statusLabel(c.status),
    'Running KMs': toNumber(c.cab_readings?.running_kms) ?? toNumber(c.distances?.running_kms) ?? '',
    'Toll Charges': toNumber(c.amounts?.toll_charges) ?? '',
    'Other Charges': toNumber(c.amounts?.other_charges) ?? '',
    'Submitted Amount': toNumber(c.amounts?.hiring_total_amount) ?? '',
    'Per KM Amount': toNumber(c.amounts?.hiring_per_km_amount) ?? '',
    'Approval Amount': toNumber(c.amounts?.approved_amount) ?? '',
    'Approver Name': c.approver_name || '',
    'Approved Date': formatDateValue(c.approved_at, ''),
  });

  const fetchExportData = async () => {
    // Exports exactly what the table shows: the applied filters.
    if (!applied) return;
    try {
      setExporting(true);
      let currentPage = 1;
      let totalPages = 1;
      const pageSize = 100;
      const allClaims: CabClaimRow[] = [];

      do {
        const response = await axiosInstance.get('/reports/claims/cab-hiring', {
          params: buildParams(applied, currentPage, pageSize),
        });
        if (!response.data.success) break;
        const payload = response.data.data || { claims: [] };
        const rows: CabClaimRow[] = payload.claims || [];
        allClaims.push(...rows);
        const p = payload.pagination || {};
        totalPages =
          Number(p.total_pages) ||
          Math.max(1, Math.ceil((Number(p.total_items ?? p.total_count) || allClaims.length) / pageSize));
        // Stop if a page comes back empty, otherwise a bad total_pages loops forever
        if (rows.length === 0) break;
        currentPage += 1;
      } while (currentPage <= totalPages);

      if (allClaims.length === 0) {
        toast.error('No cab hiring claims to export');
        return;
      }

      setExportData(allClaims.map(mapClaimForExport));
      setShowExcelModal(true);
    } catch (error) {
      console.error(error);
      toast.error('Failed to prepare cab hiring claim export');
    } finally {
      setExporting(false);
    }
  };

  const handleApplyFilters = () => {
    const dateError = dateRangeError(draft.dateFrom, draft.dateTo, 'Trip start date range');
    if (dateError) {
      toast.error(dateError);
      return;
    }
    setExpandedRow(null);
    setPage(1);
    // A new object every time, so Apply re-runs the query even if unchanged.
    setApplied({ ...draft });
    setShowFilterForm(false);
  };

  const handleResetDraftFilters = () => setDraft(EMPTY_FILTERS);

  const handleClearFilters = () => {
    setDraft(EMPTY_FILTERS);
    setApplied(null);
    setClaims([]);
    setExpandedRow(null);
    setShowFilterForm(true);
    setPage(1);
    setPagination({ total_items: 0, current_page: 1, total_pages: 1, items_per_page: limit, has_next_page: false, has_previous_page: false });
  };

  // Shown above the table so it is clear what the rows are filtered by.
  const appliedChips = applied
    ? [
        applied.search.trim() && `Search: ${applied.search.trim()}`,
        applied.status && `Status: ${statusLabel(applied.status)}`,
        applied.engineer && `Engineer: ${applied.engineer.label}`,
        (applied.dateFrom || applied.dateTo) &&
          `Trip start: ${applied.dateFrom ? formatDateValue(applied.dateFrom) : 'any'} – ${applied.dateTo ? formatDateValue(applied.dateTo) : 'any'}`,
      ].filter(Boolean) as string[]
    : [];

  const paginationAdapter: PaginationType = {
    offset: Math.max((pagination.current_page - 1) * pagination.items_per_page, 0),
    limit: pagination.items_per_page,
    page: pagination.current_page,
    total_pages: pagination.total_pages,
    total_count: pagination.total_items,
    has_next_page: pagination.has_next_page,
    has_prev_page: pagination.has_previous_page,
    next_page: pagination.has_next_page ? pagination.current_page + 1 : null,
    prev_page: pagination.has_previous_page ? pagination.current_page - 1 : null
  };

  const getStatusBadge = (s: string) => {
    const map: Record<string, string> = {
      claim_submitted: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
      in_progress: 'bg-blue-100 text-blue-700',
      completed: 'bg-indigo-100 text-indigo-700',
    };
    return map[s] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border border-gray-200 rounded p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Cab Hiring Claim Report</h1>
            <p className="text-sm text-gray-600 mt-1">View cab hiring claims with location trail &amp; distances.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!showFilterForm && (
              <button
                onClick={() => setShowFilterForm(true)}
                className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
              >
                Apply Filters Again
              </button>
            )}
            <button
              onClick={handleClearFilters}
              className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
            >
              Clear
            </button>
            <button
              onClick={fetchExportData}
              disabled={!hasSearched || exporting}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-green-400"
            >
              {exporting ? <FaSpinner className="w-4 h-4 animate-spin" /> : <FaFileExcel className="w-4 h-4" />}
              {exporting ? 'Preparing...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {showFilterForm ? (
        <div className="bg-white shadow-sm rounded border border-gray-200 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
              <div className="relative">
                <FiSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  value={draft.search}
                  onChange={(e) => setDraftField({ search: e.target.value })}
                  onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                  placeholder="Engineer name or code, vehicle, driver, purpose…"
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <select
                value={draft.status}
                onChange={(e) => setDraftField({ status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              >
                <option value="">All Status</option>
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Engineer</label>
              <SelectWithSearch2
                placeholder="Search engineer by name or code"
                value={draft.engineer}
                onChange={(option) => setDraftField({ engineer: option || null })}
                apiEndpoint="/users/list"
                minSearchLength={0}
                mapOption={mapEngineerOption}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Trip Start Date — From</label>
              <input
                type="date"
                value={draft.dateFrom}
                onChange={(e) => setDraftField({ dateFrom: e.target.value })}
                {...dateRangeInputBounds(draft.dateFrom, draft.dateTo).from}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Trip Start Date — To</label>
              <input
                type="date"
                value={draft.dateTo}
                onChange={(e) => setDraftField({ dateTo: e.target.value })}
                {...dateRangeInputBounds(draft.dateFrom, draft.dateTo).to}
                className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
              />
            </div>
            <div className="flex items-end gap-2">
              <button onClick={handleApplyFilters} className="px-4 py-2 text-sm bg-blue-800 text-white rounded hover:bg-blue-900">Apply Filters</button>
              <button onClick={handleResetDraftFilters} className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded hover:bg-gray-50">Reset</button>
            </div>
          </div>
        </div>
      ) : (
      <div className="bg-white shadow-sm rounded overflow-hidden border border-gray-200">
        <div className="flex flex-wrap items-center gap-2 px-4 py-2 border-b border-gray-200 bg-gray-50 text-xs text-gray-600">
          <span className="font-medium text-gray-700">{pagination.total_items} trip(s)</span>
          {appliedChips.length ? (
            appliedChips.map((chip) => (
              <span key={chip} className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200">{chip}</span>
            ))
          ) : (
            <span>No filters — all trips you can see</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Engineer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Project Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Trip Purpose</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Start Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">End Location</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Visits Done</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Sys. Calc. Distance</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Trip Start Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Trip End Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 whitespace-nowrap">Submitted Amount</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 whitespace-nowrap">Approval Amount</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Approver Name</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 whitespace-nowrap">Approved Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? <tr><td colSpan={TABLE_COLUMN_COUNT} className="px-4 py-10 text-center text-sm text-gray-600">Loading...</td></tr> :
                claims.length === 0 ? <tr><td colSpan={TABLE_COLUMN_COUNT} className="px-4 py-10 text-center text-sm text-gray-600">No claims found. Try widening the date range or clearing the filters.</td></tr> :
                  claims.map((c) => (
                    <React.Fragment key={c.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {c.engineer?.name || '-'}
                          {c.engineer?.employee_code && (
                            <div className="text-xs text-gray-500">{c.engineer.employee_code}</div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">{c.project_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{c.trip_purpose || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-[160px] truncate" title={c.start_address || ''}>{c.start_address || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 max-w-[160px] truncate" title={c.end_address || ''}>{c.end_address || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{c.visits_count ?? '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 font-medium">{showNumber(c.distances?.total_route_km)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDateValue(c.trip_start_date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDateValue(c.trip_end_date)}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${getStatusBadge(c.status)}`}>{statusLabel(c.status)}</span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium text-right whitespace-nowrap">{formatAmount(c.amounts?.hiring_total_amount)}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 font-medium text-right whitespace-nowrap">{formatAmount(c.amounts?.approved_amount)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700">{c.approver_name || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">{formatDateValue(c.approved_at)}</td>
                        <td className="px-4 py-3 text-sm">
                          <button onClick={() => setExpandedRow(expandedRow === c.id ? null : c.id)} className="p-2 rounded hover:bg-gray-100 text-blue-700">
                            <FiMapPin className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                      {expandedRow === c.id && (
                        <tr>
                          <td colSpan={TABLE_COLUMN_COUNT} className="px-4 py-4 bg-gray-50">
                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1"><FiNavigation className="w-4 h-4" /> Location Trail</h4>
                                  <div className="space-y-2 text-xs">
                                    {(c.location_trail || []).length === 0 ? (
                                      <div className="text-gray-500">No location trail recorded for this trip.</div>
                                    ) : (c.location_trail || []).map((loc, idx) => {
                                      const coords = loc.latitude != null && loc.longitude != null
                                        ? `${loc.latitude}, ${loc.longitude}`
                                        : 'No location captured';
                                      const nextKm = toNumber(loc.distance_to_next_km);
                                      return (
                                        <div key={`${loc.point}-${idx}`} className="bg-white border border-gray-200 rounded p-2 flex justify-between items-start">
                                          <div>
                                            <span className="font-medium text-gray-700">{loc.label}:</span>
                                            <span className="text-gray-600 ml-1">{loc.address || coords}</span>
                                            {loc.site_name && <div className="text-gray-500 mt-0.5">Site: {loc.site_name} ({loc.site_id || '-'})</div>}
                                          </div>
                                          {nextKm !== null && (
                                            <span className="text-blue-600 font-medium whitespace-nowrap ml-2">→ {nextKm} km</span>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1"><FiMapPin className="w-4 h-4" /> Cab Readings &amp; Amounts</h4>
                                  <div className="grid grid-cols-2 gap-2 text-xs">
                                    <div className="bg-white border border-gray-200 rounded p-2">
                                      <span className="text-gray-500">Start Reading</span>
                                      <div className="font-medium">{showNumber(c.cab_readings?.start_reading)}</div>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded p-2">
                                      <span className="text-gray-500">End Reading</span>
                                      <div className="font-medium">{showNumber(c.cab_readings?.end_reading)}</div>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded p-2">
                                      <span className="text-gray-500">Running KMs</span>
                                      <div className="font-medium">{showNumber(c.cab_readings?.running_kms ?? c.distances?.running_kms)}</div>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded p-2">
                                      <span className="text-gray-500">Toll Charges</span>
                                      <div className="font-medium">{formatAmount(c.amounts?.toll_charges)}</div>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded p-2">
                                      <span className="text-gray-500">Other Charges</span>
                                      <div className="font-medium">{formatAmount(c.amounts?.other_charges)}</div>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded p-2">
                                      <span className="text-gray-500">Per KM Amount</span>
                                      <div className="font-medium">{formatAmount(c.amounts?.hiring_per_km_amount)}</div>
                                    </div>
                                    <div className="bg-white border border-gray-200 rounded p-2">
                                      <span className="text-gray-500">Submitted Amount</span>
                                      <div className="font-medium">{formatAmount(c.amounts?.hiring_total_amount)}</div>
                                    </div>
                                    <div className="bg-green-50 border border-green-200 rounded p-2 col-span-2">
                                      <span className="text-gray-500">Approved Amount</span>
                                      <div className="font-semibold text-green-700">{formatAmount(c.amounts?.approved_amount)}</div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {c.photos && Object.values(c.photos).some(Boolean) && (
                                <div>
                                  <h4 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1"><FiImage className="w-4 h-4" /> Photos</h4>
                                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
                                    {[
                                      { key: 'bill_photo', label: 'Bill' },
                                      { key: 'toll_photo', label: 'Toll' },
                                      { key: 'cab_start_number_selfie', label: 'Start Number' },
                                      { key: 'cab_start_model_selfie', label: 'Start Reading Kms' },
                                      { key: 'cab_end_number_selfie', label: 'End Number' },
                                      { key: 'cab_end_model_selfie', label: 'Selfie with End Kms' },
                                    ].map(({ key, label }) => {
                                      const url = (c.photos as any)?.[key];
                                      if (!url) return null;
                                      return (
                                        <a
                                          key={key}
                                          href={url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="group flex flex-col items-center gap-1"
                                        >
                                          <img
                                            src={url}
                                            alt={label}
                                            loading="lazy"
                                            className="w-full h-20 object-cover rounded border border-gray-200 group-hover:border-blue-400 transition-colors"
                                          />
                                          <span className="text-xs text-gray-500 group-hover:text-blue-600">{label}</span>
                                        </a>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
            </tbody>
          </table>
        </div>
        <Pagination
          pagination={paginationAdapter}
          onPageChange={(nextPage) => {
            if (!hasSearched) return;
            setExpandedRow(null);
            setPage(nextPage);
          }}
          onLimitChange={(nextLimit) => {
            setPage(1);
            setLimit(nextLimit);
          }}
        />
      </div>
      )}
      <ExcelDownload
        isOpen={showExcelModal}
        onClose={() => setShowExcelModal(false)}
        data={exportData}
        filename="cab_hiring_claim_reports"
        title="Export Cab Hiring Claim Reports"
        description="Download cab hiring claim reports with current filters"
      />
    </div>
  );
};

export default CabHiringClaimReport;